import React, { useState, useEffect, useCallback } from 'react';
import Header from '../../components/Header';
import { styles, colors, radius } from '../../styles/theme';
import { normalizeForSearch } from '../../utils/searchMatch';
import { logError } from '../../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

const TABS = [
  { key: 'fiyat', label: 'Fiyat Geçmişi' },
  { key: 'stok', label: 'Stok Hareketleri' },
  { key: 'fatura', label: 'Faturalar' },
  { key: 'tedarikci', label: 'Tedarikçi Düzenleme' },
  { key: 'malzeme', label: 'Malzeme Düzenleme' },
  { key: 'kalori', label: 'Kalori Hesapla' },
  { key: 'transfer', label: 'Transfer Düzeltme' },
];

const VeriDuzeltmePage = () => {
  const [activeTab, setActiveTab] = useState('fiyat');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [hata, setHata] = useState(null);
  const [originalData, setOriginalData] = useState([]);
  const [changedIds, setChangedIds] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [columns, setColumns] = useState([]);
  const [urunKodu, setUrunKodu] = useState('');
  const [besinSonuclari, setBesinSonuclari] = useState([]);
  const [besinLoading, setBesinLoading] = useState(false);
  const [besinHata, setBesinHata] = useState(null);
  const [transferId, setTransferId] = useState('');
  const [transferPreview, setTransferPreview] = useState(null);
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferApplying, setTransferApplying] = useState(false);
  const [transferHata, setTransferHata] = useState(null);
  const [transferSonuc, setTransferSonuc] = useState(null);
  const [sonTransferler, setSonTransferler] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setHata(null);
    try {
      let url = '';
      if (activeTab === 'fiyat') {
        url = `${API_URL}/api/finance/fiyat-gecmisi?limit=200`;
      } else if (activeTab === 'stok') {
        url = `${API_URL}/api/stok-hareketleri?limit=200`;
      } else if (activeTab === 'fatura') {
        url = `${API_URL}/api/faturalar`;
      } else if (activeTab === 'tedarikci') {
        url = `${API_URL}/api/veri-duzeltme/tedarikciler`;
      } else if (activeTab === 'malzeme') {
        url = `${API_URL}/api/veri-duzeltme/malzemeler`;
      } else if (activeTab === 'kalori' || activeTab === 'transfer') {
        setData([]);
        setOriginalData([]);
        setColumns([]);
        setLoading(false);
        return;
      }
      const res = await fetch(url, { headers: getAuthHeaders() });

      if (res.status === 401) {
        localStorage.removeItem('token');
        window.location.href = '/login';
        return;
      }

      if (!res.ok) {
        throw new Error(`Sunucu hatası: ${res.status}`);
      }

      const json = await res.json();
      let rows = [];
      if (activeTab === 'fiyat') {
        rows = json.kayitlar || [];
      } else if (activeTab === 'stok') {
        rows = Array.isArray(json) ? json : json.hareketler || [];
      } else if (activeTab === 'fatura') {
        rows = Array.isArray(json) ? json : json.faturalar || [];
      } else if (activeTab === 'tedarikci' || activeTab === 'malzeme') {
        rows = json.rows || [];
        setColumns(json.columns || []);
      }

      if (activeTab !== 'tedarikci' && activeTab !== 'malzeme') {
        setColumns([]);
      }
      setData(rows);
      setOriginalData(rows.map(r => ({ ...r })));
      setChangedIds(new Set());
    } catch (err) {
      logError('Veri yüklenemedi:', err);
      setHata('Veriler yüklenirken hata oluştu. Lütfen tekrar deneyin.');
      setData([]);
    }
    setLoading(false);
  }, [activeTab]);

  useEffect(() => {
    fetchData();
    setSearch('');
    setChangedIds(new Set());
  }, [fetchData]);

  const handleDelete = async (id) => {
    let url = '';
    if (activeTab === 'fiyat') {
      url = `${API_URL}/api/finance/fiyat-gecmisi/${id}`;
    } else if (activeTab === 'stok') {
      url = `${API_URL}/api/stok-hareketleri/${id}`;
    } else if (activeTab === 'fatura') {
      url = `${API_URL}/api/faturalar/${id}`;
    }

    if (!window.confirm('Bu kaydı silmek istediğinize emin misiniz? Bu işlem geri alınamaz.')) return;

    try {
      const res = await fetch(url, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (res.status === 401) {
        localStorage.removeItem('token');
        window.location.href = '/login';
        return;
      }

      if (res.ok) {
        fetchData();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Silme başarısız');
      }
    } catch (err) {
      logError(err);
      alert('Silme hatası');
    }
  };


  const isEditableTab = activeTab === 'tedarikci' || activeTab === 'malzeme';

  const handleCellChange = (id, field, value) => {
    setData(prev => prev.map(row => (
      row.id === id ? { ...row, [field]: value } : row
    )));
    setChangedIds(prev => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  const cancelChanges = () => {
    setData(originalData.map(r => ({ ...r })));
    setChangedIds(new Set());
  };

  const buildRowChanges = row => {
    const original = originalData.find(item => item.id === row.id) || {};
    const changes = {};
    columns.forEach(column => {
      if (!column.editable) return;
      const name = column.name;
      if (row[name] !== original[name]) changes[name] = row[name];
    });
    return changes;
  };

  const handleBulkSave = async () => {
    const rows = data.filter(row => changedIds.has(row.id));
    if (!rows.length) {
      alert('Kaydedilecek değişiklik bulunmuyor.');
      return;
    }

    if (!window.confirm(`${rows.length} satırdaki değişiklikler toplu olarak kaydedilecek. Devam edilsin mi?`)) {
      return;
    }

    setSaving(true);
    setHata(null);

    const tableName = activeTab === 'tedarikci' ? 'tedarikciler' : 'malzemeler';
    const payloadRows = rows
      .map(row => ({ id: row.id, changes: buildRowChanges(row) }))
      .filter(item => Object.keys(item.changes).length > 0);

    try {
      const res = await fetch(`${API_URL}/api/veri-duzeltme/${tableName}/bulk`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ rows: payloadRows })
      });

      if (res.status === 401) {
        localStorage.removeItem('access_token');
        window.location.href = '/login';
        return;
      }

      const result = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(result.detail || `Sunucu hatası: ${res.status}`);

      alert(`${result.updated ?? payloadRows.length} kayıt başarıyla güncellendi.`);
      await fetchData();
    } catch (err) {
      setHata(err.message || 'Toplu kayıt sırasında hata oluştu.');
      alert(err.message || 'Toplu kayıt sırasında hata oluştu.');
    } finally {
      setSaving(false);
    }
  };



  const normalizeBesinRows = payload => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.rows)) return payload.rows;
    if (Array.isArray(payload?.sonuclar)) return payload.sonuclar;
    if (payload?.sonuc) return [payload.sonuc];
    if (payload && typeof payload === 'object') return [payload];
    return [];
  };

  const hesaplaBesin = async (tumUrunler = false) => {
    const kod = urunKodu.trim();
    if (!tumUrunler && !kod) {
      alert('Lütfen bir ürün kodu girin.');
      return;
    }

    setBesinLoading(true);
    setBesinHata(null);

    try {
      const url = tumUrunler
        ? `${API_URL}/api/veri-duzeltme/besin-hesapla/tumu`
        : `${API_URL}/api/veri-duzeltme/besin-hesapla?urun_kodu=${encodeURIComponent(kod)}`;

      const res = await fetch(url, { headers: getAuthHeaders() });

      if (res.status === 401) {
        localStorage.removeItem('access_token');
        window.location.href = '/login';
        return;
      }

      const result = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(result.detail || `Sunucu hatası: ${res.status}`);

      const rows = normalizeBesinRows(result);
      setBesinSonuclari(rows);

      if (!rows.length) {
        setBesinHata('Hesaplanabilecek ürün bulunamadı.');
      }
    } catch (err) {
      setBesinSonuclari([]);
      setBesinHata(err.message || 'Besin değerleri hesaplanırken hata oluştu.');
    } finally {
      setBesinLoading(false);
    }
  };


  const transferJson = async res => {
    const result = await res.json().catch(() => ({}));
    if (res.status === 401) {
      localStorage.removeItem('access_token');
      window.location.href = '/login';
      throw new Error('Oturum süresi doldu.');
    }
    if (!res.ok) throw new Error(result.detail || `Sunucu hatası: ${res.status}`);
    return result;
  };

  const loadSonTransferler = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/veri-duzeltme/transfer-duzeltme-son?limit=50`, { headers: getAuthHeaders() });
      const result = await transferJson(res);
      setSonTransferler(result.rows || []);
    } catch (err) {
      setTransferHata(err.message || 'Son transferler yüklenemedi.');
      setSonTransferler([]);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'transfer') {
      setTransferHata(null);
      setTransferSonuc(null);
      loadSonTransferler();
    }
  }, [activeTab, loadSonTransferler]);

  const transferOnizle = async (idValue = transferId) => {
    const id = String(idValue || '').trim();
    if (!/^\d+$/.test(id)) {
      setTransferHata('Geçerli bir Transfer ID girin.');
      return;
    }
    setTransferLoading(true);
    setTransferHata(null);
    setTransferSonuc(null);
    setTransferPreview(null);
    setTransferId(id);
    try {
      const res = await fetch(`${API_URL}/api/veri-duzeltme/transfer-duzeltme/${id}/preview`, { headers: getAuthHeaders() });
      setTransferPreview(await transferJson(res));
    } catch (err) {
      setTransferHata(err.message || 'Transfer önizlemesi alınamadı.');
    } finally {
      setTransferLoading(false);
    }
  };

  const transferUygula = async () => {
    if (!transferPreview?.guvenli || !transferPreview?.transfer?.id) return;
    const t = transferPreview.transfer;
    if (!window.confirm(`Transfer #${t.id} ve bağlı tüm etkileri geri alınacak.\n\n${t.kaynak} → ${t.hedef}\n${t.malzeme}: ${t.quantity} ${t.birim || ''}\n\nDevam edilsin mi?`)) return;
    setTransferApplying(true);
    setTransferHata(null);
    try {
      const res = await fetch(`${API_URL}/api/veri-duzeltme/transfer-duzeltme/${t.id}/uygula`, {
        method: 'POST', headers: getAuthHeaders()
      });
      const result = await transferJson(res);
      setTransferSonuc(result);
      setTransferPreview(null);
      setTransferId('');
      await loadSonTransferler();
    } catch (err) {
      setTransferHata(err.message || 'Transfer düzeltme işlemi başarısız.');
    } finally {
      setTransferApplying(false);
    }
  };

  const csvValue = value => `"${String(value ?? '').replace(/"/g, '""')}"`;

  const besinCSVIndir = () => {
    if (!besinSonuclari.length) {
      alert('CSV olarak indirilecek hesaplama sonucu bulunmuyor.');
      return;
    }

    const headers = [
      'Ürün Kodu',
      'Ürün Adı',
      'Tür',
      'Porsiyon / Çıktı Miktarı',
      'Birim',
      'Hesap Bazı',
      'Kalori (kcal)',
      'Yağ (g)',
      'Protein (g)',
      'Karbonhidrat (g)'
    ];

    const lines = besinSonuclari.map(row => [
      row.urun_kodu ?? row.kod,
      row.urun_adi ?? row.ad,
      row.tur ?? row.malzeme_tipi,
      row.cikti_miktari ?? row.porsiyon_miktari ?? row.miktar,
      row.birim,
      row.hesap_bazi ?? '',
      row.kalori,
      row.yag,
      row.protein,
      row.karbonhidrat
    ].map(csvValue).join(';'));

    const csv = ['sep=;', headers.map(csvValue).join(';'), ...lines].join('\r\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `besin_degerleri_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const editableInputStyle = {
    width: '100%',
    minWidth: '90px',
    padding: '7px 8px',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '13px',
    background: 'white'
  };

  const changedRowStyle = {
    background: '#fffde7'
  };

  const columnInputType = columnType => {
    const type = String(columnType || '').toLowerCase();
    if (type.includes('bool')) return 'checkbox';
    if (type.includes('int') || type.includes('numeric') || type.includes('decimal') || type.includes('float') || type.includes('double') || type.includes('real')) return 'number';
    if (type.includes('date') || type.includes('timestamp')) return 'date';
    return 'text';
  };

  const formatColumnName = name => String(name || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, letter => letter.toUpperCase());

  const renderDynamicCell = (row, column) => {
    const type = columnInputType(column.type);
    const value = row[column.name];

    if (!column.editable) {
      return <span style={{ color: '#777', fontFamily: column.name === 'id' ? 'monospace' : 'inherit' }}>{value ?? ''}</span>;
    }

    if (type === 'checkbox') {
      return (
        <input
          type="checkbox"
          checked={Boolean(value)}
          onChange={e => handleCellChange(row.id, column.name, e.target.checked)}
        />
      );
    }

    let shownValue = value ?? '';
    if (type === 'date' && shownValue) shownValue = String(shownValue).substring(0, 10);

    return (
      <input
        type={type}
        step={type === 'number' ? 'any' : undefined}
        value={shownValue}
        onChange={e => handleCellChange(row.id, column.name, e.target.value === '' ? null : e.target.value)}
        style={{ ...editableInputStyle, minWidth: type === 'text' ? '150px' : '105px' }}
      />
    );
  };

  const filteredData = data.filter(item => {
    if (!search) return true;
    const s = normalizeForSearch(search);
    const text = JSON.stringify(item).toLowerCase();
    return text.includes(s);
  });

  const cellStyle = { padding: '8px 10px', borderBottom: `1px solid ${colors.border}`, fontSize: '13px' };
  const thStyle = { ...cellStyle, fontWeight: 600, borderBottom: '2px solid #ddd', background: '#fafafa', textAlign: 'left' };
  const btnDel = { padding: '3px 10px', background: '#e53935', color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontSize: '12px' };

  return (
    <div>
      <Header title="Veri Düzeltme" />
      <div style={{ padding: '20px', background: colors.background, minHeight: 'calc(100vh - 60px)' }}>

        <div style={{ background: '#fff3e0', padding: '12px 16px', borderRadius: radius.md, marginBottom: '16px', border: '1px solid #ffcc02', fontSize: '13px' }}>
          <strong>⚠️ Dikkat:</strong> Silme işlemleri geri alınamaz. Tedarikçi ve Malzeme Düzenleme sekmelerinde hücreleri değiştirdikten sonra “Toplu Kaydet” düğmesine basılmalıdır.
        </div>

        {/* Sekmeler */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
          {TABS.map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)} style={{ padding: '10px 20px', background: activeTab === t.key ? styles.primary : 'white', color: activeTab === t.key ? 'white' : '#333', border: `1px solid ${colors.border}`, borderRadius: radius.sm, cursor: 'pointer', fontWeight: activeTab === t.key ? 600 : 400 }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Arama */}
        {activeTab !== 'kalori' && activeTab !== 'transfer' && (
          <div style={{ marginBottom: '12px' }}>
            <input
              placeholder="Ara... (malzeme, tedarikçi, fatura no vb.)"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ padding: '8px 12px', width: '350px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px' }}
            />
            <span style={{ marginLeft: '12px', fontSize: '13px', color: '#777' }}>
              {filteredData.length} kayıt
            </span>
          </div>
        )}


        {isEditableTab && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', padding: '10px 12px', background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: radius.md }}>
            <strong style={{ fontSize: '13px' }}>
              {changedIds.size} değiştirilmiş satır
            </strong>
            <button
              onClick={handleBulkSave}
              disabled={saving || changedIds.size === 0}
              style={{
                padding: '8px 16px',
                background: saving || changedIds.size === 0 ? '#bdbdbd' : '#2e7d32',
                color: 'white',
                border: 'none',
                borderRadius: radius.sm,
                cursor: saving || changedIds.size === 0 ? 'not-allowed' : 'pointer',
                fontWeight: 600
              }}
            >
              {saving ? 'Kaydediliyor...' : 'Toplu Kaydet'}
            </button>
            <button
              onClick={cancelChanges}
              disabled={saving || changedIds.size === 0}
              style={{
                padding: '8px 16px',
                background: 'white',
                color: '#555',
                border: `1px solid ${colors.border}`,
                borderRadius: radius.sm,
                cursor: saving || changedIds.size === 0 ? 'not-allowed' : 'pointer'
              }}
            >
              Değişiklikleri Geri Al
            </button>
            <span style={{ color: '#666', fontSize: '12px' }}>
              Sarı satırlar henüz kaydedilmemiş değişiklikleri gösterir.
            </span>
          </div>
        )}

        {/* Hata mesajı */}
        {hata && (
          <div style={{ background: '#ffebee', padding: '12px 16px', borderRadius: radius.md, marginBottom: '16px', border: '1px solid #ffcdd2', fontSize: '13px', color: '#c62828' }}>
            {hata}
            <button onClick={fetchData} style={{ marginLeft: '12px', padding: '4px 12px', background: '#c62828', color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontSize: '12px' }}>
              Tekrar Dene
            </button>
          </div>
        )}

        {/* Kalori / Besin Değeri Hesaplama */}
        {activeTab === 'kalori' && (
          <div style={{ background: colors.surface, borderRadius: radius.md, padding: '18px', marginBottom: '16px' }}>
            <div style={{ marginBottom: '14px' }}>
              <h3 style={{ margin: '0 0 6px 0' }}>Reçeteden Besin Değeri Hesaplama</h3>
              <div style={{ color: '#666', fontSize: '13px' }}>
                Mamul ve Ürünlerin kalori, yağ, protein ve karbonhidrat değerleri reçete ve alt reçeteler açılarak hesaplanır.
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '16px' }}>
              <input
                value={urunKodu}
                onChange={e => setUrunKodu(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') hesaplaBesin(false);
                }}
                placeholder="Ürün kodu girin"
                style={{ padding: '9px 12px', width: '240px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px' }}
              />
              <button
                onClick={() => hesaplaBesin(false)}
                disabled={besinLoading}
                style={{ padding: '9px 16px', background: besinLoading ? '#bdbdbd' : styles.primary, color: 'white', border: 'none', borderRadius: radius.sm, cursor: besinLoading ? 'not-allowed' : 'pointer', fontWeight: 600 }}
              >
                Ürün Kodunu Hesapla
              </button>
              <button
                onClick={() => hesaplaBesin(true)}
                disabled={besinLoading}
                style={{ padding: '9px 16px', background: besinLoading ? '#bdbdbd' : '#6a1b9a', color: 'white', border: 'none', borderRadius: radius.sm, cursor: besinLoading ? 'not-allowed' : 'pointer', fontWeight: 600 }}
              >
                Tüm Mamul ve Ürünleri Hesapla
              </button>
              <button
                onClick={besinCSVIndir}
                disabled={!besinSonuclari.length || besinLoading}
                style={{ padding: '9px 16px', background: !besinSonuclari.length || besinLoading ? '#bdbdbd' : '#2e7d32', color: 'white', border: 'none', borderRadius: radius.sm, cursor: !besinSonuclari.length || besinLoading ? 'not-allowed' : 'pointer', fontWeight: 600 }}
              >
                CSV Olarak İndir
              </button>
            </div>

            {besinHata && (
              <div style={{ background: '#ffebee', padding: '10px 12px', border: '1px solid #ffcdd2', borderRadius: radius.sm, color: '#c62828', marginBottom: '12px', fontSize: '13px' }}>
                {besinHata}
              </div>
            )}

            {besinLoading ? (
              <div style={{ padding: '30px', textAlign: 'center', color: colors.textTertiary }}>Reçeteler ve alt reçeteler hesaplanıyor...</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Ürün Kodu</th>
                      <th style={thStyle}>Ürün Adı</th>
                      <th style={thStyle}>Tür</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Çıktı Miktarı</th>
                      <th style={thStyle}>Birim</th>
                      <th style={thStyle}>Hesap Bazı</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Kalori (kcal)</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Yağ (g)</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Protein (g)</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Karbonhidrat (g)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {besinSonuclari.map((row, index) => (
                      <tr key={`${row.urun_kodu ?? row.kod ?? 'urun'}-${index}`}>
                        <td style={{ ...cellStyle, fontFamily: 'monospace' }}>{row.urun_kodu ?? row.kod ?? '-'}</td>
                        <td style={cellStyle}>{row.urun_adi ?? row.ad ?? '-'}</td>
                        <td style={cellStyle}>{row.tur ?? row.malzeme_tipi ?? '-'}</td>
                        <td style={{ ...cellStyle, textAlign: 'right' }}>{row.cikti_miktari ?? row.porsiyon_miktari ?? row.miktar ?? '-'}</td>
                        <td style={cellStyle}>{row.birim ?? '-'}</td>
                        <td style={cellStyle}>
                          <div>{row.hesap_bazi ?? '-'}</div>
                          {!!row.uyari_sayisi && (
                            <div title={(row.uyarilar || []).join('\n')} style={{ marginTop: '4px', color: '#c62828', fontSize: '11px', fontWeight: 600 }}>
                              ⚠ {row.uyari_sayisi} birim/veri uyarısı
                            </div>
                          )}
                        </td>
                        <td style={{ ...cellStyle, textAlign: 'right', fontFamily: 'monospace' }}>{Number(row.kalori ?? 0).toFixed(2)}</td>
                        <td style={{ ...cellStyle, textAlign: 'right', fontFamily: 'monospace' }}>{Number(row.yag ?? 0).toFixed(2)}</td>
                        <td style={{ ...cellStyle, textAlign: 'right', fontFamily: 'monospace' }}>{Number(row.protein ?? 0).toFixed(2)}</td>
                        <td style={{ ...cellStyle, textAlign: 'right', fontFamily: 'monospace' }}>{Number(row.karbonhidrat ?? 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {!besinSonuclari.length && !besinHata && (
                  <div style={{ padding: '30px', textAlign: 'center', color: colors.textTertiary }}>
                    Hesaplama yapmak için ürün kodu girin veya tüm ürünleri hesaplayın.
                  </div>
                )}
              </div>
            )}
          </div>
        )}


        {/* Transfer Düzeltme */}
        {activeTab === 'transfer' && (
          <div style={{ display: 'grid', gap: '16px' }}>
            <div style={{ background: colors.surface, borderRadius: radius.md, padding: '18px', border: `1px solid ${colors.border}` }}>
              <h3 style={{ margin: '0 0 6px' }}>Transfer ve Tüm Etkilerini Geri Al</h3>
              <div style={{ color: '#666', fontSize: '13px', marginBottom: '14px' }}>
                Transfer, şirketler arası satış, stok hareketleri, stok bakiyesi ve tüketim raporu birlikte kontrol edilir.
              </div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <input value={transferId}
                  onChange={e => setTransferId(e.target.value.replace(/[^\d]/g, ''))}
                  onKeyDown={e => { if (e.key === 'Enter') transferOnizle(); }}
                  placeholder="Transfer ID (örn. 1066)"
                  style={{ padding: '9px 12px', width: '230px', border: `1px solid ${colors.border}`, borderRadius: radius.sm }} />
                <button onClick={() => transferOnizle()} disabled={transferLoading || !transferId}
                  style={{ padding: '9px 16px', background: transferLoading || !transferId ? '#bdbdbd' : styles.primary, color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontWeight: 600 }}>
                  {transferLoading ? 'Kontrol Ediliyor...' : 'Önizle'}
                </button>
              </div>
              {transferHata && <div style={{ marginTop: '12px', padding: '10px', background: '#ffebee', color: '#c62828', borderRadius: radius.sm }}>{transferHata}</div>}
              {transferSonuc && <div style={{ marginTop: '12px', padding: '10px', background: '#e8f5e9', color: '#166534', borderRadius: radius.sm }}>
                <strong>✓ Transfer düzeltildi.</strong> Stok hareketi: {transferSonuc.silinen_stok_hareketi}, internal sale: {transferSonuc.silinen_internal_sales}, transfer: {transferSonuc.silinen_transfer}.
                <div>Tüketim raporu: {transferSonuc.rapor_tuketim_yenilendi ? 'yeniden hesaplandı.' : `yenilenemedi: ${transferSonuc.rapor_hata || '-'}`}</div>
              </div>}
            </div>

            {transferPreview && (
              <div style={{ background: colors.surface, borderRadius: radius.md, padding: '18px', border: `1px solid ${transferPreview.guvenli ? '#a5d6a7' : '#ef9a9a'}` }}>
                <h3 style={{ marginTop: 0 }}>Transfer #{transferPreview.transfer.id}</h3>
                <div style={{ fontWeight: 700 }}>{transferPreview.transfer.kaynak} → {transferPreview.transfer.hedef}</div>
                <div>{transferPreview.transfer.transfer_tarihi} · {transferPreview.transfer.malzeme_kod} - {transferPreview.transfer.malzeme}</div>
                <div style={{ fontSize: '18px', fontWeight: 700, marginTop: '5px' }}>{Number(transferPreview.transfer.quantity || 0).toLocaleString('tr-TR')} {transferPreview.transfer.birim || ''}</div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: '10px', marginTop: '16px' }}>
                  <div style={{ padding: '10px', background: '#fafafa' }}>Internal Sale: <strong>{transferPreview.internal_sales?.length || 0}</strong></div>
                  <div style={{ padding: '10px', background: '#fafafa' }}>Stok Hareketi: <strong>{transferPreview.stok_hareketleri?.length || 0}</strong></div>
                  <div style={{ padding: '10px', background: '#fafafa' }}>Rapor Kaydı: <strong>{transferPreview.rapor_tuketim_kayit_sayisi || 0}</strong></div>
                </div>

                <div style={{ marginTop: '12px', padding: '10px', borderRadius: radius.sm, background: transferPreview.guvenli ? '#e8f5e9' : '#ffebee', color: transferPreview.guvenli ? '#166534' : '#c62828' }}>
                  {transferPreview.uyari || (transferPreview.stok_durumu === 'TUTARLI'
                    ? '✓ Stok hareketleri transfer ile birebir uyumlu.'
                    : transferPreview.stok_durumu === 'ONCEDEN_TEMIZLENMIS'
                      ? '✓ Stok hareketleri daha önce temizlenmiş; stok tekrar değiştirilmeyecek.'
                      : `⚠ ${transferPreview.stok_durumu}`)}
                </div>

                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '14px' }}>
                  <thead><tr><th style={thStyle}>Lokasyon</th><th style={thStyle}>Mevcut</th><th style={thStyle}>Sonrası</th></tr></thead>
                  <tbody>{(transferPreview.stoklar || []).map(r => <tr key={r.lokasyon_id}>
                    <td style={cellStyle}>{r.lokasyon}</td>
                    <td style={cellStyle}>{Number(r.mevcut || 0).toFixed(3)}</td>
                    <td style={cellStyle}><strong>{Number(r.sonraki || 0).toFixed(3)}</strong></td>
                  </tr>)}</tbody>
                </table>

                <button onClick={transferUygula} disabled={!transferPreview.guvenli || transferApplying}
                  style={{ marginTop: '16px', padding: '10px 16px', background: !transferPreview.guvenli || transferApplying ? '#bdbdbd' : '#c62828', color: 'white', border: 'none', borderRadius: radius.sm, fontWeight: 700 }}>
                  {transferApplying ? 'Uygulanıyor...' : 'Transferi ve Tüm Etkilerini Geri Al'}
                </button>
              </div>
            )}

            <div style={{ background: colors.surface, borderRadius: radius.md, padding: '18px' }}>
              <h3 style={{ marginTop: 0 }}>Son Transferler</h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr><th style={thStyle}>ID</th><th style={thStyle}>Tarih</th><th style={thStyle}>Kaynak → Hedef</th><th style={thStyle}>Malzeme</th><th style={thStyle}>Miktar</th><th style={thStyle}>İşlem</th></tr></thead>
                  <tbody>{sonTransferler.map(r => <tr key={r.id}>
                    <td style={cellStyle}>{r.id}</td><td style={cellStyle}>{r.transfer_tarihi || '-'}</td>
                    <td style={cellStyle}>{r.kaynak} → {r.hedef}</td><td style={cellStyle}>{r.malzeme_kod} - {r.malzeme}</td>
                    <td style={cellStyle}>{Number(r.quantity || 0).toLocaleString('tr-TR')} {r.birim || ''}</td>
                    <td style={cellStyle}><button onClick={() => transferOnizle(r.id)} style={{ padding: '5px 10px', cursor: 'pointer' }}>Önizle</button></td>
                  </tr>)}</tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* İçerik */}
        {activeTab !== 'kalori' && activeTab !== 'transfer' && (
        <div style={{ background: colors.surface, borderRadius: radius.md, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: colors.textTertiary }}>Yükleniyor...</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                {activeTab === 'fiyat' && (
                  <>
                    <thead>
                      <tr>
                        <th style={thStyle}>ID</th>
                        <th style={thStyle}>Tarih</th>
                        <th style={thStyle}>Malzeme</th>
                        <th style={thStyle}>Tedarikçi</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Birim Fiyat</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Miktar</th>
                        <th style={thStyle}>Kaynak</th>
                        <th style={{ ...thStyle, textAlign: 'center', width: '70px' }}>Sil</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredData.map(r => (
                        <tr key={r.id}>
                          <td style={{ ...cellStyle, fontFamily: 'monospace', color: colors.textTertiary, fontSize: '12px' }}>{r.id}</td>
                          <td style={cellStyle}>{r.fiyat_tarihi}</td>
                          <td style={cellStyle}>{r.malzeme_ad}</td>
                          <td style={cellStyle}>{r.tedarikci_ad}</td>
                          <td style={{ ...cellStyle, textAlign: 'right', fontFamily: 'monospace' }}>{Number(r.birim_fiyat).toFixed(2)}</td>
                          <td style={{ ...cellStyle, textAlign: 'right' }}>{r.miktar} {r.malzeme_birim}</td>
                          <td style={cellStyle}>
                            <span style={{ padding: '2px 6px', borderRadius: radius.lg, fontSize: '11px', background: r.kaynak_tipi === 'MANUAL' ? '#e3f2fd' : '#f3e5f5' }}>
                              {r.kaynak_tipi}
                            </span>
                          </td>
                          <td style={{ ...cellStyle, textAlign: 'center' }}>
                            <button onClick={() => handleDelete(r.id)} style={btnDel}>Sil</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </>
                )}

                {activeTab === 'stok' && (
                  <>
                    <thead>
                      <tr>
                        <th style={thStyle}>ID</th>
                        <th style={thStyle}>Tarih</th>
                        <th style={thStyle}>Hareket No</th>
                        <th style={thStyle}>Malzeme</th>
                        <th style={thStyle}>Tip</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Miktar</th>
                        <th style={thStyle}>Kaynak</th>
                        <th style={thStyle}>Açıklama</th>
                        <th style={{ ...thStyle, textAlign: 'center', width: '70px' }}>Sil</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredData.map(r => (
                        <tr key={r.id}>
                          <td style={{ ...cellStyle, fontFamily: 'monospace', color: colors.textTertiary, fontSize: '12px' }}>{r.id}</td>
                          <td style={cellStyle}>{r.hareket_tarihi ? r.hareket_tarihi.substring(0, 10) : '-'}</td>
                          <td style={{ ...cellStyle, fontFamily: 'monospace', fontSize: '12px' }}>{r.hareket_no}</td>
                          <td style={cellStyle}>{r.malzeme_ad || (r.malzeme && r.malzeme.ad) || '-'}</td>
                          <td style={cellStyle}>
                            <span style={{ padding: '2px 6px', borderRadius: radius.lg, fontSize: '11px', background: (r.hareket_tipi || '').includes('GIRIS') ? '#e8f5e9' : '#ffebee' }}>
                              {r.hareket_tipi}
                            </span>
                          </td>
                          <td style={{ ...cellStyle, textAlign: 'right', fontFamily: 'monospace', color: (r.miktar || 0) >= 0 ? '#2e7d32' : '#c62828' }}>
                            {r.miktar > 0 ? '+' : ''}{r.miktar}
                          </td>
                          <td style={cellStyle}>{r.referans_tipi || '-'}</td>
                          <td style={{ ...cellStyle, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.açıklama || '-'}</td>
                          <td style={{ ...cellStyle, textAlign: 'center' }}>
                            <button onClick={() => handleDelete(r.id)} style={btnDel}>Sil</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </>
                )}

                {activeTab === 'fatura' && (
                  <>
                    <thead>
                      <tr>
                        <th style={thStyle}>ID</th>
                        <th style={thStyle}>Fatura No</th>
                        <th style={thStyle}>Tarih</th>
                        <th style={thStyle}>Tedarikçi</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Tutar</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Toplam</th>
                        <th style={thStyle}>Durum</th>
                        <th style={{ ...thStyle, textAlign: 'center', width: '70px' }}>Sil</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredData.map(r => (
                        <tr key={r.id}>
                          <td style={{ ...cellStyle, fontFamily: 'monospace', color: colors.textTertiary, fontSize: '12px' }}>{r.id}</td>
                          <td style={{ ...cellStyle, fontFamily: 'monospace' }}>{r.fatura_no}</td>
                          <td style={cellStyle}>{r.fatura_tarihi}</td>
                          <td style={cellStyle}>{r.tedarikci_ad || (r.tedarikçi && r.tedarikçi.unvan) || '-'}</td>
                          <td style={{ ...cellStyle, textAlign: 'right', fontFamily: 'monospace' }}>{Number(r.tutar || 0).toFixed(2)}</td>
                          <td style={{ ...cellStyle, textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>{Number(r.toplam_tutar || 0).toFixed(2)}</td>
                          <td style={cellStyle}>
                            <span style={{ padding: '2px 6px', borderRadius: radius.lg, fontSize: '11px', background: r.durum === 'ODENDI' ? '#e8f5e9' : r.durum === 'IPTAL' ? '#ffebee' : '#fff3e0' }}>
                              {r.durum}
                            </span>
                          </td>
                          <td style={{ ...cellStyle, textAlign: 'center' }}>
                            <button onClick={() => handleDelete(r.id)} style={btnDel}>Sil</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </>
                )}

                {isEditableTab && (
                  <>
                    <thead>
                      <tr>
                        {columns.map(column => (
                          <th key={column.name} style={thStyle}>
                            {formatColumnName(column.name)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredData.map(row => (
                        <tr key={row.id} style={changedIds.has(row.id) ? changedRowStyle : undefined}>
                          {columns.map(column => (
                            <td key={column.name} style={cellStyle}>
                              {renderDynamicCell(row, column)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </>
                )}
              </table>

              {!loading && filteredData.length === 0 && !hata && (
                <div style={{ padding: '40px', textAlign: 'center', color: colors.textTertiary }}>
                  Kayıt bulunamadı
                </div>
              )}
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
};

export default VeriDuzeltmePage;
